import { type ReactNode, useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import { MoreHorizontal, Pin, PinOff, Plus, Trash2 } from 'lucide-react'
import DashboardLayout from '@/components/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { PostListItem } from '@/types'

type Props = {
  posts: PostListItem[]
}

function stripHtml(html: string): string {
  if (typeof window === 'undefined') return html.replace(/<[^>]*>/g, '').trim()
  const tmp = document.createElement('div')
  tmp.innerHTML = html
  return (tmp.textContent || tmp.innerText || '').trim()
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text
}

function relativeTime(iso: string): string {
  const date = new Date(iso)
  const diff = (Date.now() - date.getTime()) / 1000
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function RowActions({ post, onRequestDelete }: { post: PostListItem; onRequestDelete: () => void }) {
  function togglePin(e: Event) {
    e.preventDefault()
    const url = post.pinned_at ? `/dashboard/posts/${post.id}/unpin` : `/dashboard/posts/${post.id}/pin`
    router.post(url, {}, { preserveScroll: true })
  }

  function handleDeleteSelect(e: Event) {
    e.preventDefault()
    onRequestDelete()
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Actions">
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>
          <Link href={`/dashboard/posts/${post.id}/edit`}>Edit</Link>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={togglePin}>
          {post.pinned_at ? (
            <>
              <PinOff className="mr-2 size-3.5" />
              Unpin
            </>
          ) : (
            <>
              <Pin className="mr-2 size-3.5" />
              Pin to top
            </>
          )}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={handleDeleteSelect} className="text-destructive focus:text-destructive">
          <Trash2 className="mr-2 size-3.5" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function DeletePostDialog({ post, onClose }: { post: PostListItem | null; onClose: () => void }) {
  const [submitting, setSubmitting] = useState(false)
  const open = post !== null

  const handleOpenChange = (next: boolean) => {
    if (submitting) return
    if (!next) onClose()
  }

  const handleConfirm = () => {
    if (!post) return
    setSubmitting(true)
    router.delete(`/dashboard/posts/${post.id}`, {
      preserveScroll: true,
      onFinish: () => {
        setSubmitting(false)
        onClose()
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete this post?</DialogTitle>
          <DialogDescription>
            This will remove it from your followers' feeds and your public profile. This can't be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={submitting}>Cancel</Button>
          </DialogClose>
          <Button variant="destructive" onClick={handleConfirm} disabled={submitting}>
            {submitting ? 'Deleting…' : 'Delete post'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default function DashboardPostsIndex({ posts }: Props) {
  const [deleteTarget, setDeleteTarget] = useState<PostListItem | null>(null)

  return (
    <>
      <Head title="Posts" />
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-semibold tracking-tight">Posts</h1>
            <p className="mt-1 text-base text-muted-foreground">Share updates with your followers.</p>
          </div>
          <Button asChild>
            <Link href="/dashboard/posts/new">
              <Plus className="size-4" />
              New post
            </Link>
          </Button>
        </div>

        {posts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-lg font-medium">No posts yet</p>
              <p className="mt-1 text-base text-muted-foreground">
                Publish your first update — it appears on your public profile and in followers' feeds.
              </p>
              <Button asChild className="mt-6">
                <Link href="/dashboard/posts/new">
                  <Plus className="size-4" />
                  New post
                </Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {posts.map(post => {
              const excerpt = truncate(stripHtml(post.body), 240)
              return (
                <Card key={post.id}>
                  <CardContent className="flex items-start justify-between gap-3 p-4">
                    <div className="min-w-0 flex-1">
                      <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
                        {post.pinned_at && (
                          <span className="inline-flex items-center gap-1 rounded-md border border-primary/20 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                            <Pin className="size-3" />
                            Pinned
                          </span>
                        )}
                        {post.author.name && (
                          <>
                            <span className="font-medium text-foreground">{post.author.name}</span>
                            <span>·</span>
                          </>
                        )}
                        <span>{relativeTime(post.created_at)}</span>
                        {post.edited_at && (
                          <>
                            <span>·</span>
                            <span>edited</span>
                          </>
                        )}
                      </div>
                      <p className="text-base text-foreground">{excerpt || <span className="italic text-muted-foreground">No content</span>}</p>
                    </div>
                    <RowActions post={post} onRequestDelete={() => setDeleteTarget(post)} />
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </div>
      <DeletePostDialog post={deleteTarget} onClose={() => setDeleteTarget(null)} />
    </>
  )
}

DashboardPostsIndex.layout = (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>
